/*
 * Fetch wrapper for the AquaStar customer web app.
 *
 * Mirrors the admin console's apiClient (Gateway-native auth, JWT in
 * sessionStorage, dispatcher-envelope unwrap), with one difference:
 * `getFn` and `postFn` accept `{ public: true }` to skip the
 * Authorization header. The invite landing page calls
 * `aquastarInviteInfo` and `aquastarInviteAccept` BEFORE the user has
 * a token — those handlers are gated `auth: 'public'` in the manifest.
 */

import { APP_ID, GATEWAY_BASE, appAliasUrl, appFnUrl } from './config';
import { lockedSignInMessage } from './accountLock';

const TOKEN_KEY = 'aquastar.user.jwt';

/** Set on a 401 so the Login page can show "your session timed out". */
export const SESSION_EXPIRED_KEY = 'aquastar.user.sessionExpired';
/** Event the AuthProvider listens for to redirect to /login on expiry. */
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';
// Guard so a burst of concurrent 401s only signals once.
let sessionExpiredSignaled = false;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
    // New session — re-arm the expiry signal + clear any stale timeout flag.
    sessionExpiredSignaled = false;
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  } else {
    sessionStorage.removeItem(TOKEN_KEY);
    // A null access token means logged out → drop the refresh token too.
    sessionStorage.removeItem(REFRESH_KEY);
  }
}

function authHeader(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ─── Silent refresh (P1-JWT02) ────────────────────────────────────────────
// The refresh token is the long-lived (~7d) credential that mints fresh
// short-lived access tokens. Stored in sessionStorage (tab-scoped; bearer
// model — cookie-mode is deferred per PLAN_NEXUS_PHASE_2B_VI_AUTH §10).
const REFRESH_KEY = 'aquastar.user.refresh';

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}
export function setRefreshToken(token: string | null): void {
  if (token) sessionStorage.setItem(REFRESH_KEY, token);
  else sessionStorage.removeItem(REFRESH_KEY);
}

// Single-flight: concurrent 401s share ONE /auth/refresh call. The refresh
// token is single-use and rotates on each use (server-side theft-detection),
// so parallel refreshes would consume the same token and trip a family-revoke
// → logging everyone out. One in-flight refresh; the rest await it.
let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    // window.fetch (NOT authedFetch) — must not recurse on its own 401.
    const res = await window.fetch(`${GATEWAY_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!res.ok) { setToken(null); return false; }
    const data = (await res.json().catch(() => null)) as
      | { accessToken?: string; refreshToken?: string }
      | null;
    if (!data?.accessToken) { setToken(null); return false; }
    setToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken); // rotation
    return true;
  } catch {
    return false; // network error — leave tokens; caller's 401 path handles it
  }
}
function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/**
 * fetch wrapper that transparently recovers an expired access token. On a 401
 * for an AUTHED request (Authorization header present) that has a stored
 * refresh token, it runs a single-flight /auth/refresh then RETRIES the request
 * once with the new token. If refresh fails, the original 401 is returned so
 * handle() runs the normal logout path. Unauthed calls (login, public
 * handlers) have no Authorization header and pass straight through.
 */
async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const res = await window.fetch(input, init);
  if (res.status !== 401) return res;
  const headers = new Headers(init.headers as HeadersInit | undefined);
  if (!headers.has('Authorization') || !getRefreshToken()) return res;
  const ok = await refreshAccessToken();
  if (!ok) return res;
  const t = getToken();
  if (t) headers.set('Authorization', `Bearer ${t}`);
  return window.fetch(input, { ...init, headers });
}

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, `Non-JSON response: ${text.slice(0, 200)}`);
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    // Token expired/invalid → clear it, flag a session timeout, and signal the
    // app to redirect to /login instead of leaving a raw error on screen.
    setToken(null);
    if (typeof window !== 'undefined' && !sessionExpiredSignaled) {
      sessionExpiredSignaled = true;
      sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    }
  }
  if (!res.ok) {
    const body = await readJson<{ error?: string; message?: string }>(res).catch(
      () => undefined,
    );
    const msg =
      body?.message ?? body?.error ?? `HTTP ${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg, body);
  }
  return readJson<T>(res);
}

export interface GatewayUser {
  userId: string;
  email: string;
  givenName?: string;
  displayName?: string;
  status?: 'active' | 'disabled' | 'pending_verification' | string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken?: string;
  user: GatewayUser;
  mfaRequired?: boolean;
}

export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const res = await fetch(`${GATEWAY_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID }),
  });
  let body: LoginResponse;
  try {
    body = await handle<LoginResponse>(res);
  } catch (err) {
    // AQUASTAR-542: say until when, and that a password reset unlocks now,
    // instead of the server's "too many failed attempts; account
    // temporarily locked".
    const eb = err instanceof ApiError ? (err.body as { code?: string; lockedUntil?: string | null } | undefined) : undefined;
    if (err instanceof ApiError && eb?.code === 'ACCOUNT_LOCKED') {
      throw new ApiError(err.status, lockedSignInMessage(eb.lockedUntil), err.body);
    }
    throw err;
  }

  if (body.mfaRequired) {
    throw new ApiError(
      200,
      'Multi-factor authentication is required for this account. MFA is not yet supported on the web — please use the mobile app to sign in.',
      body,
    );
  }
  if (body.user?.status === 'pending_verification') {
    throw new ApiError(
      200,
      'Email verification is pending for this account. Please verify your email before signing in.',
      body,
    );
  }
  if (!body.accessToken) {
    throw new ApiError(200, 'Login response missing accessToken.', body);
  }

  setToken(body.accessToken);
  if (body.refreshToken) setRefreshToken(body.refreshToken);
  return body;
}

/**
 * Server response from POST /auth/register when self-registration is enabled.
 * Note: there is NO accessToken in the response — the user has not yet
 * verified their email, so no session is issued. The caller must
 * subsequently collect the OTP from the user and call verifyEmail(),
 * then login() with the original credentials to establish a session.
 *
 * `code` values:
 *   'OK'                   — new user created, verification code emailed
 *   'PENDING_VERIFICATION' — email already registered but unverified;
 *                             a fresh verification code was emailed
 *                             (server treats this as a 409 but the
 *                             register() function below upgrades it to
 *                             a return value so the Signup UI can show
 *                             the same OTP-entry step in both cases)
 */
export interface RegisterResponse {
  code: 'OK' | 'PENDING_VERIFICATION';
  userId: string;
  email: string;
  message: string;
}

/**
 * POST /auth/register on the Gateway.
 *
 * Two-step signup pattern: this call creates the user with
 * `email_verified=false` and mints a 6-character OTP that the server
 * emails to the user. The caller MUST follow up with verifyEmail()
 * once the user enters the code, then login() to get a session.
 *
 * Hard 409s (account exists AND already verified) bubble up as
 * ApiError(409, 'EMAIL_EXISTS', ...) — the UI should route those to a
 * "sign in instead" prompt. Soft 409s (account exists but unverified)
 * are normalized here into a RegisterResponse with
 * code='PENDING_VERIFICATION' so the UI can drive the OTP-entry step
 * uniformly across both new and resumed signups.
 */
export async function register(
  email: string,
  password: string,
  displayName?: string,
): Promise<RegisterResponse> {
  const res = await fetch(`${GATEWAY_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      password,
      appId: APP_ID,
      ...(displayName ? { displayName } : {}),
    }),
  });
  // Handle PENDING_VERIFICATION soft-409 specially: server says "you
  // already registered but never verified; here's a fresh code" —
  // surface that to the UI as a successful first step so the OTP
  // entry screen renders the same way.
  if (res.status === 409) {
    const body = await res
      .clone()
      .json()
      .catch(() => null);
    if (body?.code === 'PENDING_VERIFICATION') {
      return body as RegisterResponse;
    }
    // Otherwise fall through to the standard 409 → ApiError path.
  }
  return handle<RegisterResponse>(res);
}

/**
 * POST /auth/verify-email — consume the 6-character OTP the user
 * received via email after register(). On success the user's
 * email_verified flag is flipped server-side; the caller should then
 * call login() with the original credentials to get a session.
 *
 * Server returns 400 INVALID_CODE on wrong-or-expired OTP (deliberately
 * same code as wrong-OTP to avoid an enumeration oracle on which emails
 * exist).
 */
export async function verifyEmail(email: string, code: string): Promise<void> {
  const res = await fetch(`${GATEWAY_BASE}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  await handle<{ ok: true; alreadyVerified?: boolean }>(res);
}

/**
 * POST /auth/resend-verification — ask the server to mint a fresh OTP
 * for an unverified account and re-email it. The server returns 200
 * regardless of whether the email exists (anti-enumeration), so success
 * here just means "request accepted" — there's no way for the client to
 * tell whether anything was actually sent. The UI should display a
 * generic "If your email is registered, a new code is on its way"
 * message either way.
 */
export async function resendVerification(email: string): Promise<void> {
  const res = await fetch(`${GATEWAY_BASE}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  await handle<{ ok: true }>(res);
}

export function logout(): void {
  setToken(null);
}

/**
 * POST /auth/forgot-password — emails the user a 6-digit reset code if
 * the address is registered. The Gateway always returns 200 with the
 * same message regardless of whether the email exists, to avoid timing
 * a user-enumeration oracle, so success here just means "the request
 * was accepted" — not "we sent you a code."
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetch(`${GATEWAY_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  await handle<{ message?: string }>(res);
}

/**
 * POST /auth/reset-password — consume a 6-digit code from a reset
 * email and set a new password. Server policy: minimum 8 chars,
 * verified server-side; surface the policy message on 400 so the UI
 * doesn't have to duplicate the rules.
 */
export async function resetPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const res = await fetch(`${GATEWAY_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, newPassword }),
  });
  await handle<{ message?: string }>(res);
}

/**
 * POST /auth/change-password — authed. Confirms the user knows the
 * current password before accepting the new one. Same policy enforcement
 * as reset.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await authedFetch(`${GATEWAY_BASE}/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  await handle<{ message?: string }>(res);
}

/**
 * Build the URL that initiates the OAuth flow on the Gateway. Browser
 * navigates here; the Gateway redirects to the provider, then the
 * provider redirects back to the Gateway's callback endpoint, which
 * issues a JWT and redirects to `redirectUri` with the tokens in the
 * URL fragment (`#access_token=…&refresh_token=…&id_token=…`).
 *
 * Fragment, not query string — fragments don't reach servers, so the
 * JWT doesn't end up in proxy logs / referrer headers.
 *
 * The SPA's `consumeOAuthFragment()` (below) reads the fragment on
 * mount and stores the access token via setToken().
 */
export function oauthAuthorizeUrl(
  provider: 'google' | 'github' | 'microsoft' | 'apple',
  redirectUri: string,
): string {
  const params = new URLSearchParams({
    redirect_uri: redirectUri,
    app_id: APP_ID,
  });
  return `${GATEWAY_BASE}/auth/oauth/${provider}?${params}`;
}

export interface OAuthFragmentResult {
  ok: boolean;
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  provider?: string;
  error?: string;
}

/**
 * Parse and consume an OAuth callback fragment on the current page,
 * if present. Returns the result and clears the fragment from the URL
 * so a refresh doesn't re-process it.
 *
 *   #oauth_result=success&access_token=…&id_token=…&refresh_token=…&provider=google
 *   #oauth_result=error&oauth_error=…
 *
 * Side-effect: on success, calls setToken() with the access token so
 * the rest of the app sees the user as signed in.
 */
export function consumeOAuthFragment(): OAuthFragmentResult | null {
  const hash = window.location.hash || '';
  if (!hash.startsWith('#') || !hash.includes('oauth_result=')) return null;
  const params = new URLSearchParams(hash.slice(1));
  const result = params.get('oauth_result');

  // Clear the fragment from history so a refresh doesn't reprocess it.
  // Use replaceState so the back-button history isn't polluted.
  const cleanUrl =
    window.location.pathname + window.location.search;
  window.history.replaceState(null, '', cleanUrl);

  if (result === 'error') {
    return { ok: false, error: params.get('oauth_error') ?? 'Google sign-in failed.' };
  }
  if (result === 'success') {
    const accessToken = params.get('access_token') ?? undefined;
    const refreshToken = params.get('refresh_token') ?? undefined;
    if (accessToken) setToken(accessToken);
    if (refreshToken) setRefreshToken(refreshToken); // enable silent refresh post-OAuth
    return {
      ok: true,
      accessToken,
      idToken: params.get('id_token') ?? undefined,
      refreshToken,
      provider: params.get('provider') ?? undefined,
    };
  }
  return null;
}

export interface AuthMe {
  userId: string;
  email: string;
  emailVerified?: boolean;
  status?: string;
  givenName?: string;
  familyName?: string;
  displayName?: string;
  image?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  timeZone?: string | null;
  createdAt?: string;
}

export async function getMe(): Promise<AuthMe> {
  const res = await authedFetch(`${GATEWAY_BASE}/auth/me`, {
    method: 'GET',
    headers: { ...authHeader() },
  });
  return handle<AuthMe>(res);
}

/** Update the signed-in user's profile (display name parts + avatar image URL). */
export async function updateProfile(patch: {
  givenName?: string | null;
  familyName?: string | null;
  image?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  timeZone?: string | null;
}): Promise<AuthMe> {
  const res = await authedFetch(`${GATEWAY_BASE}/auth/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(patch),
  });
  return handle<AuthMe>(res);
}

/** Upload a file to the Nexus file service; returns a tokenised same-origin URL. */
export async function uploadAppFile(
  file: File,
): Promise<{ id: string; url: string; sizeBytes: number }> {
  const dataBase64 = await fileToBase64(file);
  const res = await authedFetch(`${GATEWAY_BASE}/v1/app/${APP_ID}/files`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ filename: file.name, contentType: file.type || null, dataBase64 }),
  });
  return handle<{ id: string; url: string; sizeBytes: number }>(res);
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result);
      resolve(s.includes(',') ? s.slice(s.indexOf(',') + 1) : s);
    };
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

// ─── Platform invitations (P1-INV11b, 2026-05-21) ────────────────────────
// These two helpers replace the per-app aquastarInviteInfo /
// aquastarInviteAccept handlers — invites now live on the platform.
// `inviteInfo` is public; `inviteAccept` requires a Bearer JWT.

export interface InviteInfoResponse {
  inviterDisplayName: string | null;
  inviteeEmail: string;
  intendedRole: 'admin' | 'support' | 'sales' | string | null;
  appId: string;
  emailExists: boolean;
  accepted: boolean;
  revoked: boolean;
  expired: boolean;
}

export interface InviteAcceptResponse {
  accepted: boolean;
  alreadyAccepted: boolean;
  appId: string;
  intendedRole: 'admin' | 'support' | 'sales' | string | null;
  staffGranted: boolean;
}

export async function inviteInfo(token: string): Promise<InviteInfoResponse> {
  const url = new URL(`${GATEWAY_BASE}/auth/invite/info`, window.location.origin);
  url.searchParams.set('token', token);
  const res = await fetch(url.toString(), { method: 'GET' });
  return handle<InviteInfoResponse>(res);
}

export async function inviteAccept(token: string): Promise<InviteAcceptResponse> {
  const res = await authedFetch(`${GATEWAY_BASE}/auth/invite/accept`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    body: JSON.stringify({ token }),
  });
  return handle<InviteAcceptResponse>(res);
}

/**
 * Claim an invite as a brand-new user — atomically sets the password,
 * marks email_verified=true (the invite click IS the proof), activates
 * the account, claims the invited_role, and returns a fresh session.
 * Unlike inviteAccept(), this is unauthenticated — the token IS the
 * auth. After success the SPA should adopt the returned accessToken
 * (we call setToken() here so consumers don't have to remember).
 *
 * Returns 409 ALREADY_ACTIVE if the target user already has a
 * password — that flow goes through normal /auth/login + inviteAccept
 * instead (the SignIn branch on InviteLanding handles this).
 */
export interface InviteClaimResponse extends LoginResponse {
  appId: string;
  intendedRole: 'admin' | 'support' | 'sales' | string | null;
  staffGranted: boolean;
}

export async function inviteClaim(input: {
  token: string;
  password: string;
  givenName?: string;
  familyName?: string;
}): Promise<InviteClaimResponse> {
  const res = await fetch(`${GATEWAY_BASE}/auth/invite/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = await handle<InviteClaimResponse>(res);
  if (body.accessToken) setToken(body.accessToken);
  if (body.refreshToken) setRefreshToken(body.refreshToken);
  return body;
}

/**
 * The dispatcher envelope (carried over from the admin console — same
 * Gateway, same shape).
 */
interface AppFnEnvelope<T> {
  success: boolean;
  message?: string[] | string | null;
  data: T | null;
}

function unwrapAppFn<T>(envelope: AppFnEnvelope<T> | T): T {
  if (
    envelope !== null &&
    typeof envelope === 'object' &&
    'success' in envelope &&
    'data' in envelope
  ) {
    const env = envelope as AppFnEnvelope<T>;
    if (env.success === false) {
      const msg = Array.isArray(env.message)
        ? env.message.join(' ')
        : env.message ?? 'Handler returned success=false';
      throw new ApiError(200, msg, env);
    }
    return env.data as T;
  }
  return envelope as T;
}

interface FnOptions {
  /**
   * Skip the Authorization header. Required for handlers gated
   * `auth: 'public'` in the manifest (e.g. aquastarInviteInfo,
   * aquastarInviteAccept) which the invite landing page calls before
   * the user has a token.
   */
  public?: boolean;
}

/** Call an app-scoped GET handler. */
export async function getFn<T>(
  name: string,
  params?: Record<string, string | number | undefined>,
  opts?: FnOptions,
): Promise<T> {
  const url = new URL(appFnUrl(name), window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = opts?.public ? {} : { ...authHeader() };
  const res = await authedFetch(url.toString(), { method: 'GET', headers });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/**
 * Call an app-scoped GET via a registered alias path (the manifest
 * declares trigger.aliases[]). Required for handlers that take a
 * trailing path segment — the canonical `/fn/<name>` route is
 * declared as `appFunctionsRouter.all('/:appId/fn/:fnName')` and
 * does NOT match a trailing segment, so calling
 * `/fn/deviceInfo/<MAC>` returns "Function not found". The alias
 * middleware rewrites e.g. `/devices/device-info/<MAC>` to
 * `/fn/deviceInfo` AND populates `req.appFunctionParams.deviceId`
 * which the handler reads via `ctx.request.params.deviceId`.
 *
 *   getFnViaAlias<Device>(`/devices/device-info/${encodeURIComponent(mac)}`)
 *   → GET /v1/app/aquastar/devices/device-info/<mac>
 */
export async function getFnViaAlias<T>(
  aliasPath: string,
  opts?: FnOptions,
): Promise<T> {
  const url = appAliasUrl(aliasPath);
  const headers: Record<string, string> = opts?.public ? {} : { ...authHeader() };
  const res = await authedFetch(url, { method: 'GET', headers });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/**
 * Call an app-scoped POST/PUT/DELETE via a registered alias path.
 * Same rationale as getFnViaAlias for the URL shape.
 */
export async function fnViaAlias<T>(
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  aliasPath: string,
  body?: unknown,
  opts?: FnOptions,
): Promise<T> {
  const url = appAliasUrl(aliasPath);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts?.public ? {} : authHeader()),
  };
  const res = await authedFetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/** Call an app-scoped POST handler. */
export async function postFn<T>(
  name: string,
  body?: unknown,
  opts?: FnOptions,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts?.public ? {} : authHeader()),
  };
  const res = await authedFetch(appFnUrl(name), {
    method: 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return unwrapAppFn<T>(await handle<AppFnEnvelope<T> | T>(res));
}

/**
 * Call an app-scoped REST endpoint that is NOT an app_function — e.g. the
 * Issue Tracker routes at /v1/app/:appId/issues/*. These return raw JSON (no
 * AppFnEnvelope), so we share the same auth + 401 + error handling as
 * getFn/postFn but skip the envelope unwrap. The customer's bearer JWT marks
 * them as a client server-side, so they only ever see their own issues + the
 * public projection.
 *
 *   appRest<{ items: Issue[] }>('GET', '/issues')
 *   appRest<Issue>('POST', '/issues', { typeKey, title })
 *
 * `path` is appended to `/v1/app/<appId>` and must start with '/'.
 */
export async function appRest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${GATEWAY_BASE}/v1/app/${APP_ID}${path}`;
  const res = await authedFetch(url, {
    method,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return handle<T>(res);
}
